import { createTheme } from '@mui/material/styles';
import { surface, brand, ink, status } from './tokens';

// FARM THEME - warm earth-toned theme for the public pages.
// Colours come from ./tokens, where each one is contrast-checked against the
// surfaces it is used on. scripts/check-contrast.js enforces it in CI.

const farmTheme = createTheme({
  palette: {
    primary: {
      main: brand.brown,
      // was #B7AA9E, a 2.01:1 taupe that leaked into text via primary.light
      light: '#6E5C54',
      dark: brand.brownDark,
      contrastText: ink.onDark,
    },
    secondary: {
      // a surface colour, not a text colour -- see NEVER_TEXT_ON_LIGHT in tokens
      main: brand.mustard,
      light: '#E6C73A',
      dark: brand.mustardInk,
      contrastText: ink.onMustard,
    },
    tertiary: {
      main: brand.olive,
      light: '#A4B36B',
      dark: brand.oliveInk,
      contrastText: ink.onDark,
    },
    background: {
      default: surface.page,
      // cards lift off the page instead of blending into it
      paper: surface.card,
    },
    success: {
      main: status.success,
      light: '#81C784',
      dark: '#2E5226',
    },
    warning: {
      main: status.warning,
      light: '#FFB74D',
      dark: '#6B4606',
    },
    error: {
      main: status.error,
      light: '#ef5350',
      dark: '#7E231A',
    },
    info: {
      main: status.info,
      light: '#64b5f6',
      dark: '#1B4763',
    },
    text: {
      primary: ink.primary,   // 9.48 on page, 10.72 on card
      // was #B7AA9E at 2.01:1 -- the single biggest readability problem on the
      // site, inherited by every text.secondary in ~61 files
      secondary: ink.secondary, // 5.79 / 6.54
      disabled: ink.muted,      // 4.57 / 5.17
    },
  },
  typography: {
    // Variants deliberately do NOT set `color`. They used to, and a variant's
    // colour beats the `color` inherited from a parent -- so every unstyled
    // <Typography> on a dark panel rendered brown-on-brown. The closing CTA on
    // the farm stay page was #4B3A37 text on a #4B3A37 panel: 1.00:1, invisible.
    // Leaving colour unset lets CssBaseline's text.primary apply on light
    // surfaces and inheritance work on dark ones.
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(75, 58, 55, 0.2)', // Deep Earth Brown shadow
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #D8B510 0%, #E6C73A 100%)', // Mustard Yellow gradient
          color: ink.primary, // Deep Earth Brown text
          '&:hover': {
            background: 'linear-gradient(135deg, #B8970D 0%, #D8B510 100%)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(75, 58, 55, 0.04)', // Deep Earth Brown hover
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: surface.card,
          boxShadow: '0 4px 20px rgba(75, 58, 55, 0.08)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px rgba(75, 58, 55, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        // Every filled chip was olive with off-white text -- 2.70:1, so the
        // "Featured" badge and every other filled chip was barely legible.
        // Dark ink on the same olive is 5.82:1 and keeps the colour.
        filled: {
          backgroundColor: brand.olive,
          color: ink.onMustard,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D8B510', // Mustard Yellow on hover
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4B3A37', // Deep Earth Brown on focus
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: ink.primary,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#8A9B4F', // Olive Green for drawer background
          color: '#F2F1EC', // Off-white text
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
});

export default farmTheme;